<script setup>
import { Link, router } from '@inertiajs/vue3';
import { computed, ref, getCurrentInstance } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Tag from 'primevue/tag';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const confirm = useConfirm();
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    title: String,
    categories: Object,
});


const categoriesData = computed(() => {
    return props.categories.data || [];
});

const loading = ref(false);

const destroy = (category) => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} ${category.name}?`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('categories.destroy', category.id), {
                preserveScroll: true,
                preserveState: true,
                onStart: () => showLoading('Deleting category...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({ severity: 'success', summary: 'Success', detail: 'Category deleted successfully', life: 3000 });
                },
                onError: () => {
                    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete category', life: 3000 });
                }
            });
        }
    });
};

const sortField = ref('name');
const sortOrder = ref(1);

const fetchCategories = (page = null) => {
    const params = {};

    if (page) {
        params.page = page;
    }

    // Spatie QueryBuilder format: '-field' for descending
    if (sortField.value) {
        params.sort = sortOrder.value === -1 ? `-${sortField.value}` : sortField.value;
    }

    router.get(route('categories.index'), params, {
        preserveState: true,
        preserveScroll: true,
        onStart: () => showLoading('Loading categories...'),
        onFinish: () => hideLoading()
    });
};

const onPage = (event) => {
    fetchCategories(event.page + 1);
};

const onSort = (event) => {
    sortField.value = event.sortField;
    sortOrder.value = event.sortOrder;
    fetchCategories();
};

const paginatorTemplate = {
    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
    CurrentPageReport: 'Showing {first} to {last} of {totalRecords} entries'
};
</script>

<template>
    <AdminMainLayout>
        <ConfirmDialog />
        
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-semibold text-gray-900">{{ $t('Categories') }}</h2>
            <Link :href="route('categories.create')">
                <Button :label="$t('Add Category')" icon="pi pi-plus" class="p-button-success" />
            </Link>
        </div>

        <DataTable 
            :value="categoriesData" 
            :paginator="true" 
            :rows="15"
            :totalRecords="categories.total"
            :lazy="true"
            @page="onPage"
            @sort="onSort"
            :sortField="sortField"
            :sortOrder="sortOrder"
            :loading="loading"
            dataKey="id"
            responsiveLayout="scroll"
            :paginatorTemplate="paginatorTemplate.layout"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            class="p-datatable-sm">
            

            <template #empty>
                <div class="text-center py-4">{{ $t('No categories found.') }}</div>
            </template>

            <Column field="name" :header="$t('Name')" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    <Link :href="route('categories.edit', slotProps.data.id)" class="text-blue-600 hover:text-blue-800 font-medium">
                        {{ slotProps.data.name }}
                    </Link>
                </template>
            </Column>

            <Column field="sites_count" :header="$t('Sites')" :sortable="true" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.sites_count || 0" severity="info" />
                </template>
            </Column>

            <Column :header="$t('Actions')" :exportable="false" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="flex gap-2">
                        <Link :href="route('categories.edit', slotProps.data.id)">
                            <Button icon="pi pi-pencil" class="p-button-rounded p-button-text p-button-info" 
                                v-tooltip.top="$t('Edit')" />
                        </Link>
                        <Button icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger" 
                            @click="destroy(slotProps.data)" v-tooltip.top="$t('Delete')" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </AdminMainLayout>
</template>

<style scoped>
.card {
    background: white;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}
</style>